import { getConfiguredGoogleMapID } from '@/features/map/googleMapConfig.web';
import { locationStore } from '@/lib/location/location';
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import * as React from 'react';
import { View } from 'react-native';

type Coordinate = {
  latitude: number;
  longitude: number;
};

export function LocationPreview({
  coordinate,
  hasAddress,
}: {
  coordinate: Coordinate;
  hasAddress: boolean;
}) {
  return (
    <View
      style={{
        overflow: 'hidden',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'var(--border)',
        height: 360,
        backgroundColor: 'var(--card)',
      }}>
      <MapContent
        coordinate={coordinate}
        hasAddress={hasAddress}
      />
    </View>
  );
}

function MapContent({
  coordinate,
  hasAddress,
}: {
  coordinate: Coordinate;
  hasAddress: boolean;
}) {
  const map = useMap();

  const userCenter = React.useMemo(() => {
    const { coords } = locationStore.getState();
    return { lat: coords.latitude, lng: coords.longitude };
  }, []);

  const hasPinnedCoord =
    Number.isFinite(coordinate.latitude) &&
    Number.isFinite(coordinate.longitude) &&
    !(coordinate.latitude === 0 && coordinate.longitude === 0);

  const pinPosition = hasPinnedCoord
    ? { lat: coordinate.latitude, lng: coordinate.longitude }
    : userCenter;

  // Pan to the geocoded address when an address search resolves
  React.useEffect(() => {
    if (!map || !hasPinnedCoord || !hasAddress) return;
    map.panTo({ lat: coordinate.latitude, lng: coordinate.longitude });
    map.setZoom(16);
  }, [coordinate.latitude, coordinate.longitude, hasPinnedCoord, hasAddress, map]);

  return (
    <Map
      defaultCenter={userCenter}
      defaultZoom={13}
      mapId={getConfiguredGoogleMapID()}
      renderingType="VECTOR"
      gestureHandling="none"
      clickableIcons={false}
      disableDefaultUI
      style={{ width: '100%', height: '100%' }}
    >
      <AdvancedMarker
        position={pinPosition}
      >
        <PinElement />
      </AdvancedMarker>
    </Map>
  );
}

function PinElement() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'default',
        opacity: 1,
      }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          backgroundColor: 'var(--primary)',
          border: '3px solid #ffffff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
        </svg>
      </div>
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '8px solid var(--primary)',
          marginTop: -2,
        }}
      />
    </div>
  );
}
